import { useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMetaData } from './useMetaData.js'
import { useAblyChannel } from '../hooks/useAblyChannel.js'
import { useUIStore } from '../store/uiStore.js'
import { sendDmMessage, DEFAULT_META } from '../data/metaApi.js'

/**
 * Key for a DM thread — sorted participant names joined by commas.
 * This guarantees "Ricky,Katie" and "Katie,Ricky" resolve to the same thread.
 */
export function dmKey(participants) {
  return [...participants].sort().join(',')
}

/**
 * useDms — read + mutate direct-message threads.
 *
 * Sends go through the dedicated /meta/dms partial-update endpoint for
 * speed + safety. Receivers get the full message payload over Ably
 * (event name 'dm') so messages appear without any refetch delay.
 *
 * Pass `participants` = array of OTHER participants (not including the
 * current user — that's added automatically). For a 1-on-1 with Katie,
 * pass `['Katie']`. For a group DM, pass all others.
 */
export function useDms(otherParticipants) {
  const currentUser = useUIStore((s) => s.currentUser) || 'Unknown'
  const dmSeen = useUIStore((s) => s.dmSeen)
  const markSeen = useUIStore((s) => s.markDmSeen)
  const { data: meta } = useMetaData()
  const queryClient = useQueryClient()

  const participants = otherParticipants ? [currentUser, ...otherParticipants] : []
  const key = participants.length > 1 ? dmKey(participants) : null
  const thread = (key && meta.dms?.[key]) || null
  const messages = thread?.messages || []

  const newId = () =>
    Date.now().toString(36) + Math.random().toString(36).slice(2, 5)

  const sendMutation = useMutation({
    mutationFn: async ({ message }) =>
      sendDmMessage({ threadKey: key, participants, message }),
    onMutate: async ({ message }) => {
      // Optimistic: append to the thread immediately
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META
      const prevThread = previous.dms?.[key] || { participants, messages: [] }
      const nextMessages = prevThread.messages.some((m) => m.id === message.id)
        ? prevThread.messages
        : [...prevThread.messages, message]
      queryClient.setQueryData(['meta'], {
        ...previous,
        dms: {
          ...(previous.dms || {}),
          [key]: { ...prevThread, participants, messages: nextMessages },
        },
      })
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })

  const sendMessage = (text) => {
    if (!key || !text?.trim()) return
    const message = {
      id: newId(),
      author: currentUser,
      text: text.trim(),
      at: new Date().toISOString(),
    }
    sendMutation.mutate({ message })
    // Our own sent message is considered seen immediately
    markSeen(key, (messages.length || 0) + 1)
  }

  const seenCount = key ? dmSeen[key] || 0 : 0
  const unreadCount = Math.max(
    0,
    messages.filter((m) => m.author !== currentUser).length -
      messages.slice(0, seenCount).filter((m) => m.author !== currentUser).length
  )

  const markAsRead = () => {
    if (!key) return
    markSeen(key, messages.length)
  }

  return {
    thread,
    key,
    messages,
    sendMessage,
    unreadCount,
    markAsRead,
  }
}

/**
 * useAllDms — read-only list of every thread the current user is part of.
 * Useful for a badge-count summary or a future inbox view.
 */
export function useAllDms() {
  const currentUser = useUIStore((s) => s.currentUser)
  const dmSeen = useUIStore((s) => s.dmSeen)
  const { data: meta } = useMetaData()

  if (!currentUser) return { threads: [], totalUnread: 0 }

  const myThreads = Object.entries(meta.dms || {})
    .filter(([, t]) => (t?.participants || []).includes(currentUser))
    .map(([key, t]) => {
      const other = (t.participants || []).filter((p) => p !== currentUser)
      const seen = dmSeen[key] || 0
      const msgs = t.messages || []
      const unread = Math.max(
        0,
        msgs.filter((m) => m.author !== currentUser).length -
          msgs.slice(0, seen).filter((m) => m.author !== currentUser).length
      )
      return { key, thread: t, other, unread, lastAt: msgs[msgs.length - 1]?.at }
    })
    .sort((a, b) => (b.lastAt || '').localeCompare(a.lastAt || ''))

  const totalUnread = myThreads.reduce((n, t) => n + t.unread, 0)

  return { threads: myThreads, totalUnread }
}

/**
 * useDmRealtimeSync — app-root hook. Listens for `dm` Ably events with
 * the full message payload and inserts each into the React Query cache
 * directly — no refetch, no waiting, no KV propagation delay. Falls
 * back to the meta-changed polling path if Ably is unavailable.
 *
 * Mount exactly once at the app root.
 */
export function useDmRealtimeSync() {
  const queryClient = useQueryClient()

  useAblyChannel('dm', (data) => {
    if (!data?.threadKey || !data?.message) return
    queryClient.setQueryData(['meta'], (prev) => {
      const meta = prev || DEFAULT_META
      const threadKey = data.threadKey
      const current = meta.dms?.[threadKey] || {
        participants: data.participants || [],
        messages: [],
      }
      // Dedupe — if we already have this message (e.g. from a prior
      // local optimistic write on our own client), skip.
      if (current.messages.some((m) => m.id === data.message.id)) return meta
      const nextThread = {
        ...current,
        participants:
          current.participants && current.participants.length > 0
            ? current.participants
            : data.participants || [],
        messages: [...current.messages, data.message],
      }
      return {
        ...meta,
        dms: { ...(meta.dms || {}), [threadKey]: nextThread },
      }
    })
  })
}
